import React from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

export interface SidebarSection {
  id: string;
  title: string;
  icon: React.ReactNode;
}

interface ReviewSidebarProps {
  title: string;
  sections: SidebarSection[];
  completionStatus: Record<string, boolean>;
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
}

const ReviewSidebar: React.FC<ReviewSidebarProps> = ({
  title,
  sections,
  completionStatus,
  activeSection,
  onSectionClick,
}) => {
  const completeCount = Object.values(completionStatus).filter(Boolean).length;

  return (
    <aside className="w-64 sticky top-4 h-fit hidden md:block flex-shrink-0">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
          {title}
        </h3>
        <nav className="space-y-1">
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            const isComplete = completionStatus[section.id] === true;

            return (
              <button
                key={section.id}
                onClick={() => onSectionClick(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all
                  ${isActive
                    ? 'bg-kielis-gold text-kielis-navy font-medium shadow-sm'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <span className={isActive ? 'text-kielis-navy' : 'text-gray-500'}>
                  {section.icon}
                </span>
                <span className="flex-1 text-left truncate">{section.title}</span>
                {isComplete ? (
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-600">
            {completeCount} of {sections.length} sections complete
          </div>
        </div>
      </div>
    </aside>
  );
};

export default ReviewSidebar;
